import { UpdateAvatar } from "@/app/actions/auth/profile";
import { User } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { Button } from "../ui/button";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Flex } from "@radix-ui/themes";
import { AlertTriangle, Loader2 } from "lucide-react";
import AvatarInitials from "../shared/AvatarInitials";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export default function ChangeAvatar({
  user,
  updateUser,
  children,
}: {
  user?: User | null;
  updateUser: () => void;
  children?: any;
}) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatar, setAvatar] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const updateAvatar = async () => {
    setAvatarError(null);
    if (!avatar) {
      setAvatarError("Please select an image");
      return;
    }
    setIsLoading(true);

    const { error } = await UpdateAvatar(avatar);

    if (error) {
      console.error(error);
      setAvatarError(typeof error === "string" ? error : error.message);
      setIsLoading(false);

      return;
    }

    setOpen(false);

    updateUser();
    setIsLoading(false);
  };

  useEffect(() => {
    if (avatar) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(avatar);
    } else {
      setPreview(null);
    }
  }, [avatar]);

  return (
    <Drawer onOpenChange={setOpen} open={open}>
      <DrawerTrigger>{children}</DrawerTrigger>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="text-center">Change Avatar</DrawerTitle>
          <DrawerDescription className="text-center">
            Your avatar is used to identify you across the platform.
          </DrawerDescription>
        </DrawerHeader>
        <Flex className="flex flex-col items-center justify-center p-4">
          {avatarError && (
            <Alert variant="destructive" className="mb-4 mt-4">
              <AlertTriangle className="h-6 w-6" />
              <AlertTitle className="!pl-8">Error</AlertTitle>
              <AlertDescription className="!pl-8">
                {avatarError}
              </AlertDescription>
            </Alert>
          )}
          <AvatarInitials
            size="lg"
            firstName={user?.user_metadata?.full_name}
            lastName={user?.user_metadata?.last_name}
            image={preview || user?.user_metadata?.avatar_url}
          />
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="picture">Avatar</Label>
            <Input
              id="picture"
              type="file"
              onChange={(e) =>
                e.target.files ? setAvatar(e.target.files[0]) : null
              }
            />
          </div>
        </Flex>
        <DrawerFooter className="justify-start">
          <Button
            type="button"
            variant="default"
            onClick={() => updateAvatar()}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update
          </Button>

          <DrawerClose asChild>
            <Button variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
